import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { GenericAgentConversation } from "../types/agentConfig";

/**
 * Generic state for agent conversations that can handle any agent type
 */
export interface GenericAgentConversationsState {
  conversationsByKey: {
    [key: string]: GenericAgentConversation[];
  };
  loadingStates: {
    [key: string]: "idle" | "loading" | "succeeded" | "failed";
  };
  errorMessages: {
    [key: string]: string | null;
  };
}

const initialState: GenericAgentConversationsState = {
  conversationsByKey: {},
  loadingStates: {},
  errorMessages: {},
};

/**
 * Generate a unique key for agent conversations
 */
export const generateConversationKey = (
  agentType: string, 
  contextId: string, 
  userId?: string
): string => {
  const base = `${agentType}:${contextId}`;
  return userId ? `${base}:${userId}` : base;
};

const genericAgentConversationsSlice = createSlice({
  name: "genericAgentConversations",
  initialState,
  reducers: {
    setConversationsLoading: (
      state,
      action: PayloadAction<{ 
        agentType: string; 
        contextId: string; 
        userId?: string; 
      }>
    ) => {
      const { agentType, contextId, userId } = action.payload;
      const key = generateConversationKey(agentType, contextId, userId);
      state.loadingStates[key] = "loading";
      state.errorMessages[key] = null;
    },
    setConversationsSuccess: (
      state,
      action: PayloadAction<{
        agentType: string;
        contextId: string;
        userId?: string;
        conversations: GenericAgentConversation[];
      }>
    ) => {
      const { agentType, contextId, userId, conversations } = action.payload;
      const key = generateConversationKey(agentType, contextId, userId);
      state.conversationsByKey[key] = conversations;
      state.loadingStates[key] = "succeeded";
      state.errorMessages[key] = null;
    },
    setConversationsError: (
      state,
      action: PayloadAction<{
        agentType: string;
        contextId: string;
        userId?: string;
        error: string;
      }>
    ) => {
      const { agentType, contextId, userId, error } = action.payload;
      const key = generateConversationKey(agentType, contextId, userId);
      state.loadingStates[key] = "failed";
      state.errorMessages[key] = error;
    },
    clearConversations: (
      state,
      action: PayloadAction<{
        agentType: string;
        contextId: string;
        userId?: string;
      }>
    ) => {
      const { agentType, contextId, userId } = action.payload;
      const key = generateConversationKey(agentType, contextId, userId);
      delete state.conversationsByKey[key];
      delete state.loadingStates[key];
      delete state.errorMessages[key];
    },
  },
});

export const {
  setConversationsLoading,
  setConversationsSuccess,
  setConversationsError,
  clearConversations,
} = genericAgentConversationsSlice.actions;

export default genericAgentConversationsSlice.reducer;